import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";

interface ManageAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: "validator" | "supervisor";
  data: any[];
  onAssign: (row: any) => void;
  onUnassign: (row: any) => void;
  loading?: boolean;
  onSearch?: (search: string) => void;
}

export default function ManageAssignmentDialog({
  open,
  onOpenChange,
  type,
  data,
  onAssign,
  onUnassign,
  loading = false,
  onSearch,
}: ManageAssignmentDialogProps) {
  const [search, setSearch] = React.useState("");
  const [assignmentState, setAssignmentState] = React.useState<Record<string, boolean>>({});

  // Initialize local assignment state when modal opens
  React.useEffect(() => {
    if (open && data?.length) {
      const initialState = data.reduce(
        (acc, item) => ({ ...acc, [item.id]: !!item.isAssigned }),
        {}
      );
      setAssignmentState(initialState);
    }
    if (!open) setAssignmentState({});
  }, [open, data]);

  const filteredData = React.useMemo(() => {
    const query = search.toLowerCase();
    return data?.filter(
      (item) =>
        item?.name?.toLowerCase().includes(query) ||
        item?.email?.toLowerCase().includes(query)
    );
  }, [data, search]);

  const handleToggleAssign = (row: any) => {
    const currentlyAssigned = assignmentState[row.id];
    setAssignmentState((prev) => ({ ...prev, [row.id]: !currentlyAssigned }));
    currentlyAssigned ? onUnassign(row) : onAssign(row);
  };

  const columns: TableColumn[] = [
    { header: "Emp ID", key: "employeeId" },
    { header: "Name", key: "name" },
    { header: "Email", key: "email" },
    { header: "Active Cycles Assigned", key: "assignedDate" },
    {
      header: "Actions",
      key: "actions",
      render: (value, row) => {
        const isAssigned = assignmentState[row.id];
        return (
          <Button
            size="sm"
            variant={isAssigned ? "outline" : "default"}
            onClick={() => handleToggleAssign(row)}
          >
            {isAssigned ? "Unassign" : "Assign"}
          </Button>
        );
      },
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            Manage {type === "validator" ? "Validators" : "Supervisors"}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Assign or unassign {type}s for this cycle.
          </p>
        </DialogHeader>

        <Input
          placeholder={`Search ${type}s by name`}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            onSearch?.(e.target.value);
          }}
          className="mb-4"
        />

        <div className="max-h-[60vh] overflow-y-scroll">
          <GenericTable rowKey="id" data={filteredData || []} columns={columns} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
